import React, { useState } from "react";

// Helpers
import MapComponent from "@components/map";
import { updateTempOffice, useAppDispatch } from "@util";
import { useDarkMode } from "@hooks/dark-mode";

const OfficeCell = ({ office, removeOffice }: OfficeCellProps) => {
  const dispatch = useAppDispatch();
  const { dark } = useDarkMode();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleDeleteClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setShowConfirmation(true);
  };

  const handleConfirmDelete = () => {
    removeOffice(office.id!);
    setShowConfirmation(false);
  };

  const handleCancelDelete = (event: React.MouseEvent) => {
    event.stopPropagation();
    setShowConfirmation(false);
  };

  return (
    <div
      onClick={() => dispatch(updateTempOffice(office))}
      className="w-full flex flex-col md:flex-row md:items-center relative p-3 md:space-x-6 hover:bg-white bg-bg border border-gray-200 rounded-lg shadow dark:border-gray-600 cursor-pointer"
    >
      <MapComponent location={office} editable={false} theme={dark ? 'dark' : 'light'} />
      <div className="flex flex-col md:justify-between w-full">
        {/* Existing content */}
        <div className="grid grid-cols-1 md:grid-cols-6 w-full items-start gap-2">
          {/* Content remains the same */}
          <div className="flex flex-col justify-between h-full col-span-3">
            <h3 className="text-lg font-semibold text-black mb-2 md:mb-3">
              {office.name}
            </h3>
            <div>
              <h6 className="text-sm gap-2 font-normal text-gray-500 dark:text-gray-400">
                Employee Count
              </h6>
              <p className="text-sm font-semibold text-black">
                {office.estimatedEmployeeCount}
              </p>
            </div>
          </div>
          <div className="flex flex-col col-span-3">
            <div className="mr-8 pr-4">
              <h6 className="text-sm font-normal text-gray-500 dark:text-gray-400">
                Address
              </h6>
              <p className="text-sm font-semibold text-black">
                {office.streetNameAndNumber}
              </p>
            </div>
            <div className="mr-8 pr-4">
              <h6 className="text-sm font-normal text-gray-500 dark:text-gray-400">
                City
              </h6>
              <p className="text-sm font-semibold text-black">{office.city}</p>
            </div>
            <div className="mr-8 pr-4">
              <h6 className="text-sm font-normal text-gray-500 dark:text-gray-400">
                ZIP Code
              </h6>
              <p className="text-sm font-semibold text-black">{office.zip}</p>
            </div>
          </div>
        </div>
      </div>
      <div
        className="absolute top- right-3 mt-6"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          onClick={handleDeleteClick}
          type="button"
          className="inline-flex items-center p-1.5 text-sm font-medium text-center text-red-600 hover:text-red-800 hover:bg-gray-200 rounded-lg focus:outline-none"
        >
          <svg
            className="w-4 h-4 mr-2"
            viewBox="0 0 14 15"
            fill="none"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              fill="currentColor"
              d="M6.09922 0.300781C5.93212 0.30087 5.76835 0.347476 5.62625 0.435378C5.48414 0.523281 5.36931 0.649009 5.29462 0.798481L4.64302 2.10078H1.59922C1.36052 2.10078 1.13161 2.1956 0.962823 2.36439C0.79404 2.53317 0.699219 2.76209 0.699219 3.00078C0.699219 3.23948 0.79404 3.46839 0.962823 3.63718C1.13161 3.80596 1.36052 3.90078 1.59922 3.90078V12.9008C1.59922 13.3782 1.78886 13.836 2.12643 14.1736C2.46399 14.5111 2.92183 14.7008 3.39922 14.7008H10.5992C11.0766 14.7008 11.5344 14.5111 11.872 14.1736C12.2096 13.836 12.3992 13.3782 12.3992 12.9008V3.90078C12.6379 3.90078 12.8668 3.80596 13.0356 3.63718C13.2044 3.46839 13.2992 3.23948 13.2992 3.00078C13.2992 2.76209 13.2044 2.53317 13.0356 2.36439C12.8668 2.1956 12.6379 2.10078 12.3992 2.10078H9.35542L8.70382 0.798481C8.62913 0.649009 8.5143 0.523281 8.37219 0.435378C8.23009 0.347476 8.06631 0.30087 7.89922 0.300781H6.09922ZM4.29922 5.70078C4.29922 5.46209 4.39404 5.23317 4.56282 5.06439C4.73161 4.8956 4.96052 4.80078 5.19922 4.80078C5.43791 4.80078 5.66683 4.8956 5.83561 5.06439C6.0044 5.23317 6.09922 5.46209 6.09922 5.70078V11.1008C6.09922 11.3395 6.0044 11.5684 5.83561 11.7372C5.66683 11.906 5.43791 12.0008 5.19922 12.0008C4.96052 12.0008 4.73161 11.906 4.56282 11.7372C4.39404 11.5684 4.29922 11.3395 4.29922 11.1008V5.70078ZM8.79922 4.80078C8.56052 4.80078 8.33161 4.8956 8.16282 5.06439C7.99404 5.23317 7.89922 5.46209 7.89922 5.70078V11.1008C7.89922 11.3395 7.99404 11.5684 8.16282 11.7372C8.33161 11.906 8.56052 12.0008 8.79922 12.0008C9.03791 12.0008 9.26683 11.906 9.43561 11.7372C9.6044 11.5684 9.69922 11.3395 9.69922 11.1008V5.70078C9.69922 5.46209 9.6044 5.23317 9.43561 5.06439C9.26683 4.8956 9.03791 4.80078 8.79922 4.80078Z"
            />
          </svg>
          Delete
        </button>
      </div>

      {/* Confirmation popup */}
      {showConfirmation && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={handleCancelDelete}
        >
          <div
            className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="text-lg font-bold mb-4">Confirm Delete</h3>
            <p className="mb-6">Are you sure you want to delete the office "{office.name}"? This action cannot be undone.</p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfficeCell;
